/**
 * First-frame gate (q-mobile-first-frame-defer-creatures-and-npc-clips). On the low/mobile
 * tier the session's first frame waits on every byte requested before it, and the enemy
 * creature rigs (~3 MB) and the NPC ambient clip pack (~0.8 MB) are not needed to draw it:
 * an enemy shows its placeholder until its rig lands, an NPC idles on the body's own clips.
 * The gate holds those loads until the player's own rig has settled and one more frame has
 * rendered (the frame the player actually sees), or until `fallbackMs` of rendered frames
 * have passed — whichever is first, so a rig that never settles never strands the enemies.
 *
 * Disabled (desktop), it is released from construction and every caller behaves exactly as
 * before. It releases ONCE per session: a later zone change loads normally.
 */

/** Rendered time after the first frame at which the gate releases regardless (ms). */
export const FIRST_FRAME_GATE_FALLBACK_MS = 5000;

export interface FirstFrameGateOptions {
  /** Hold deferred loads at all (the low quality tier). `false` ⇒ released now. */
  enabled: boolean;
  /** Release after this much rendered time even if the critical path never settles. */
  fallbackMs?: number;
}

export class FirstFrameGate {
  private readonly fallbackMs: number;
  private releasedFlag: boolean;
  private releasedAt: number | null = null;
  private firstFrameAt: number | null = null;
  private settled = false;
  private settledFrameSeen = false;
  private readonly listeners: (() => void)[] = [];

  constructor(options: FirstFrameGateOptions) {
    this.fallbackMs = options.fallbackMs ?? FIRST_FRAME_GATE_FALLBACK_MS;
    this.releasedFlag = !options.enabled;
    // The dump's before-release byte count reads resource timings; a dev build's module
    // requests overflow the default 250-entry buffer before the assets arrive.
    if (options.enabled && typeof performance !== 'undefined')
      performance.setResourceTimingBufferSize?.(4000);
  }

  /** Whether deferred loads may run now. */
  get released(): boolean {
    return this.releasedFlag;
  }

  /** `performance.now()` at release, or null (never held, or still holding). */
  get releasedAtMs(): number | null {
    return this.releasedAt;
  }

  /** The critical path (the player's own rig) has settled — loaded or fallen back. */
  markCriticalSettled(): void {
    this.settled = true;
  }

  /**
   * Run `listener` once when the gate releases — at once if it already has. A throwing
   * listener is the caller's to guard; the gate never re-runs it.
   */
  onRelease(listener: () => void): void {
    if (this.releasedFlag) listener();
    else this.listeners.push(listener);
  }

  /**
   * Call once per rendered frame, AFTER the render. Releases on the first frame rendered
   * after `markCriticalSettled` (so the settled rig has been on screen), or once
   * `fallbackMs` has passed since the first rendered frame.
   */
  frameRendered(nowMs: number): void {
    if (this.releasedFlag) return;
    this.firstFrameAt ??= nowMs;
    if (this.settled) {
      if (this.settledFrameSeen) return this.release(nowMs);
      this.settledFrameSeen = true;
    }
    if (nowMs - this.firstFrameAt >= this.fallbackMs) this.release(nowMs);
  }

  private release(nowMs: number): void {
    this.releasedFlag = true;
    this.releasedAt = nowMs;
    const waiting = this.listeners.splice(0);
    for (const listener of waiting) listener();
  }
}

/** A resource-timing row (the slice of `PerformanceResourceTiming` read here). */
export interface ResourceTimingLike {
  readonly name: string;
  readonly startTime: number;
  readonly encodedBodySize: number;
}

/** The scene-dump view of the gate (`__gcSceneDump().firstFrameGate`). */
export interface FirstFrameGateDump {
  held: boolean;
  released: boolean;
  releasedAtMs: number | null;
  /** `/assets/` bytes (encoded) whose fetch started before the release; null if never held. */
  assetBytesBeforeRelease: number | null;
  assetFilesBeforeRelease: number | null;
  /** Those files' paths (query dropped), in fetch order; null if never held. */
  assetsBeforeRelease: string[] | null;
}

/** A served asset URL (path starts `/assets/`; a dev build's `/src/game/assets/*.ts` is not). */
const SERVED_ASSET = /^(?:[a-z]+:\/\/[^/]+)?\/assets\//i;

/** Sum the `/assets/` downloads that STARTED before `releasedAtMs`, each URL once. */
export function assetBytesBefore(
  entries: readonly ResourceTimingLike[],
  releasedAtMs: number,
): { bytes: number; files: number; paths: string[] } {
  const seen = new Set<string>();
  let bytes = 0;
  for (const entry of entries) {
    if (entry.startTime >= releasedAtMs || !SERVED_ASSET.test(entry.name)) continue;
    if (seen.has(entry.name) || entry.encodedBodySize <= 0) continue;
    seen.add(entry.name);
    bytes += entry.encodedBodySize;
  }
  const paths = [...seen].map((url) => url.replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/\?.*$/, ''));
  return { bytes, files: seen.size, paths };
}

/** Dump the gate; reads the page's resource timings when it was held and has released. */
export function firstFrameGateDump(
  gate: FirstFrameGate | null,
  entries: () => readonly ResourceTimingLike[] = browserResourceEntries,
): FirstFrameGateDump {
  const releasedAtMs = gate?.releasedAtMs ?? null;
  const before = releasedAtMs === null ? null : assetBytesBefore(entries(), releasedAtMs);
  return {
    held: gate !== null && (releasedAtMs !== null || !gate.released),
    released: gate?.released ?? true,
    releasedAtMs,
    assetBytesBeforeRelease: before?.bytes ?? null,
    assetFilesBeforeRelease: before?.files ?? null,
    assetsBeforeRelease: before?.paths ?? null,
  };
}

function browserResourceEntries(): readonly ResourceTimingLike[] {
  if (typeof performance === 'undefined' || typeof performance.getEntriesByType !== 'function')
    return [];
  return performance.getEntriesByType('resource') as unknown as readonly ResourceTimingLike[];
}
